/**
 * Baboodle PDP Block
 *
 * Custom element managing three accordion buying options:
 *   - Flexible Rental  (subscription)
 *   - One-Off Rental   (calendar)
 *   - Buy Preloved     (resale)
 *
 * Configuration is read from a <script type="application/json" data-baboodle-config>
 * child that the Liquid snippet populates from Supercycle metafields and theme settings.
 *
 * Metafield structures consumed:
 *   subscription_configuration.pricing_groups[]  → variant+duration → selling plan + price
 *   calendar_configuration.rental_periods[]      → duration_count → selling plan + price
 *   resale.options[]                             → condition grades → price
 */

if (!customElements.get('baboodle-pdp-block')) {
  class BaboodlePdpBlock extends HTMLElement {
    constructor() {
      super();

      /** @type {Object|null} Parsed JSON config from Liquid */
      this.config = null;

      /** @type {Object} Current UI state */
      this.state = {
        activePanel: null,
        selectedOptions: {},         // Keyed by option index (0, 1, 2…) → selected value string
        selectedVariantId: null,     // Resolved from all selected options
        selectedCondition: null,     // 'new'|'refurbished' for rental (not currently rendered)
        selectedResaleCondition: null, // Resale grade — maps to option.name (e.g. 'A'|'B'|'C'|'D')
        selectedDuration: null,      // number of months (subscription)
        deliveryDate: null,          // Date object
        returnDate: null,            // Date object (calendar only)
      };

      /** @type {number} How many Shopify product options this product has (1–3) */
      this._optionCount = 0;

      /** @type {Object} Flatpickr instances keyed by panel name */
      this.pickers = {};

      /** @type {Object} Cached availability data keyed by variant ID */
      this.availabilityCache = {};

      /**
       * Cached resale availability keyed by `${variantId}:${globalId}` → number
       * (available_item_count from the intent endpoint). `undefined` means
       * not-yet-loaded; 0 means sold out.
       */
      this.resaleAvailabilityCache = {};

      /** @type {Function|null} FoxThemeEvents unsubscribe handle */
      this._variantUnsub = null;

      /** @type {boolean} Whether we're currently adding to cart */
      this._submitting = false;
    }

    // ═══════════════════════════════════════════════════════
    //  Lifecycle
    // ═══════════════════════════════════════════════════════

    connectedCallback() {
      this._parseConfig();
      if (!this.config) return;

      this._initState();
      this._initAccordion();
      this._renderAllPills();
      this._initDatePickers();
      this._initAddToCart();
      this._subscribeToVariantChanges();
      this._updateEverything();

      // Fetch availability for default variant
      if (this.state.selectedVariantId) {
        this._fetchAvailability(this.state.selectedVariantId);
      }
    }

    disconnectedCallback() {
      Object.values(this.pickers).forEach((p) => p && p.destroy());
      this.pickers = {};
      if (this._variantUnsub) {
        this._variantUnsub();
        this._variantUnsub = null;
      }
    }

    // ═══════════════════════════════════════════════════════
    //  Config & state bootstrap
    // ═══════════════════════════════════════════════════════

    _parseConfig() {
      const el = this.querySelector('[data-baboodle-config]');
      if (!el) {
        console.error('[BaboodlePDP] Missing data-baboodle-config');
        return;
      }
      try {
        this.config = JSON.parse(el.textContent);
      } catch (e) {
        console.error('[BaboodlePDP] Bad config JSON', e);
      }
    }

    _initState() {
      const c = this.config;

      // Determine the default panel — fall back if the desired panel doesn't exist in DOM
      let desiredPanel = c.defaultMethod || 'subscription';
      const panelExists = this.querySelector(`[data-panel="${desiredPanel}"]`);
      if (!panelExists) {
        // Fall back to the first panel in the DOM
        const firstPanel = this.querySelector('[data-panel]');
        desiredPanel = firstPanel ? firstPanel.dataset.panel : null;
      }
      this.state.activePanel = desiredPanel;

      // Determine how many Shopify product options exist (1–3)
      const optionNames = c.productOptionNames || [];
      this._optionCount = optionNames.length;

      // Set initial option selections: pick the first unique value per option level
      // (each level filtered by the preceding selections) so the UI always starts
      // with the first value in each tier regardless of Shopify's variant ordering.
      // We also pre-filter by the initial method's applicable variants so only valid
      // colours/options are selected on first load.
      const variants = c.variants || [];
      const applicableIds = this._getMethodApplicableVariantIds();
      let methodVariants = variants;
      if (applicableIds) {
        methodVariants = variants.filter((v) => applicableIds.has(String(v.id)));
      }
      // Fall back to all variants if filtering would leave nothing
      if (methodVariants.length === 0) methodVariants = variants;

      for (let i = 0; i < this._optionCount; i++) {
        const optKey = 'option' + (i + 1);
        let filtered = methodVariants;
        for (let j = 0; j < i; j++) {
          const prevKey = 'option' + (j + 1);
          const prevVal = this.state.selectedOptions[j];
          filtered = filtered.filter((v) => (j === 0 ? (v[prevKey] || v.title) : v[prevKey]) === prevVal);
        }
        // First unique value for this option level
        const seen = new Set();
        for (const v of filtered) {
          const val = i === 0 ? (v[optKey] || v.title) : v[optKey];
          if (val != null && val !== '' && !seen.has(val)) {
            this.state.selectedOptions[i] = val;
            break;
          }
        }
      }

      // Resolve variant from the chosen option combination (prefer method-filtered set)
      const match = methodVariants.find((v) => {
        for (let i = 0; i < this._optionCount; i++) {
          const key = 'option' + (i + 1);
          const selected = this.state.selectedOptions[i];
          const variantVal = i === 0 ? (v[key] || v.title) : v[key];
          if (variantVal !== selected) return false;
        }
        return true;
      });
      this.state.selectedVariantId = match ? match.id : (methodVariants[0]?.id || null);

      this.state.selectedCondition = 'new';
      this.state.selectedDuration = parseInt(c.bestPeriod, 10) || 12;
    }

    // ═══════════════════════════════════════════════════════
    //  Accordion (radio-style — one panel open at a time)
    // ═══════════════════════════════════════════════════════

    _initAccordion() {
      const panels = this.querySelectorAll('[data-panel]');
      panels.forEach((panel) => {
        if (panel.dataset.panel === this.state.activePanel) {
          this._expandPanel(panel);
        }
        panel.querySelector('[data-panel-toggle]')?.addEventListener('click', () => {
          const name = panel.dataset.panel;

          // Clicking the already-open panel is a no-op — one panel must always be open
          if (name === this.state.activePanel) return;

          const cur = this.querySelector(`[data-panel="${this.state.activePanel}"]`);
          if (cur) this._collapsePanel(cur);
          this._expandPanel(panel);
          this.state.activePanel = name;

          // Re-render option pills so only variants applicable to the new method show.
          // If the currently selected variant isn't available on the new method,
          // _renderOptionPills will auto-select the first available variant.
          this._renderAllPills();
          this._resolveVariantFromOptions();
          this._updateEverything();
        });
      });
    }

    _expandPanel(panel) {
      const toggle = panel.querySelector('[data-panel-toggle]');
      const body = panel.querySelector('[data-panel-body]');
      if (!toggle || !body) return;
      panel.classList.add('baboodle-panel--expanded');
      toggle.setAttribute('aria-expanded', 'true');
      body.hidden = false;
      body.style.height = '0px';
      body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        body.style.height = body.scrollHeight + 'px';
        body.addEventListener('transitionend', () => {
          body.style.height = '';
          body.style.overflow = '';
        }, { once: true });
      });
    }

    _collapsePanel(panel) {
      const toggle = panel.querySelector('[data-panel-toggle]');
      const body = panel.querySelector('[data-panel-body]');
      if (!toggle || !body) return;
      panel.classList.remove('baboodle-panel--expanded');
      toggle.setAttribute('aria-expanded', 'false');
      body.style.height = body.scrollHeight + 'px';
      body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        body.style.height = '0px';
        body.addEventListener('transitionend', () => {
          body.hidden = true;
          body.style.height = '';
          body.style.overflow = '';
        }, { once: true });
      });
    }

    // ═══════════════════════════════════════════════════════
    //  Pill rendering (all types)
    // ═══════════════════════════════════════════════════════

    _renderAllPills() {
      // Render option pills for each product option level (0, 1, 2…)
      for (let i = 0; i < this._optionCount; i++) {
        this._renderOptionPills(i);
      }
      // Hide any hardcoded option containers beyond the product's actual option count
      for (let i = this._optionCount; i < 3; i++) {
        this.querySelectorAll(`[data-option${i}-pills]`).forEach((container) => {
          container.closest('.baboodle-field')?.classList.add('baboodle-field--hidden');
        });
      }
      // Rental condition selector (new/refurbished) removed — infrastructure not ready yet.
      // Resale condition grades (A/B/C/D) ARE rendered — driven by resale metafield.
      this._renderResaleConditionPills();
      this._renderDurationPills();
    }

    // ── Dynamic option pills (supports N options) ──────

    /**
     * Render deduplicated values for a given option index (0-based).
     * Each option level filters variants by all preceding option selections.
     * Hidden if only a single unique value exists (or product has "Default Title" for option 0).
     */
    _renderOptionPills(optionIndex) {
      const optionKey = 'option' + (optionIndex + 1); // option1, option2, option3

      // Filter variants by the current rental method's applicability first
      const applicableIds = this._getMethodApplicableVariantIds();
      let filteredVariants = this.config.variants || [];
      if (applicableIds) {
        filteredVariants = filteredVariants.filter((v) => applicableIds.has(String(v.id)));
      }

      // Then filter by all preceding options
      for (let i = 0; i < optionIndex; i++) {
        const prevKey = 'option' + (i + 1);
        const prevVal = this.state.selectedOptions[i];
        filteredVariants = filteredVariants.filter((v) => {
          const vVal = i === 0 ? (v[prevKey] || v.title) : v[prevKey];
          return vVal === prevVal;
        });
      }

      // Deduplicate this option's values (order-preserved)
      const seen = new Set();
      const uniqueValues = [];
      filteredVariants.forEach((v) => {
        const val = optionIndex === 0 ? (v[optionKey] || v.title) : v[optionKey];
        if (val != null && val !== '' && !seen.has(val)) {
          seen.add(val);
          uniqueValues.push(val);
        }
      });

      // Always show the option field if the product has multiple values for this option
      // (even if method filtering reduces it to one). Only hide when the product itself
      // has a single value (e.g. "Default Title") — check against the unfiltered variant list.
      const allVariantValues = new Set();
      (this.config.variants || []).forEach((v) => {
        const val = optionIndex === 0 ? (v[optionKey] || v.title) : v[optionKey];
        if (val != null && val !== '') allVariantValues.add(val);
      });
      const shouldRender = allVariantValues.size > 1;

      // When on the resale panel, compute which option values are fully sold
      // out (every underlying variant has zero stock across every applicable
      // resale option). Those pills render but are disabled.
      const soldOutValues = new Set();
      const onResale = this.state.activePanel === 'resale';
      const resalePending = onResale && this._isResaleAvailabilityPending();
      if (onResale && !resalePending) {
        uniqueValues.forEach((val) => {
          const matchingVariants = filteredVariants.filter((v) => {
            const vVal = optionIndex === 0 ? (v[optionKey] || v.title) : v[optionKey];
            return vVal === val;
          });
          const allSoldOut = matchingVariants.length > 0 &&
            matchingVariants.every((v) => this._isVariantSoldOutForResale(v.id));
          if (allSoldOut) soldOutValues.add(val);
        });
      }

      // On the resale panel we intentionally hold off on picking a default
      // until every (variant, option) availability call has returned. This
      // prevents the pill from flickering from "Variant A selected" to
      // "Variant B selected" once the sold-out data lands. For other panels
      // (and once resale availability is resolved), fall through to the
      // usual logic: keep the current value if still selectable, otherwise
      // prefer the first in-stock value, falling back to the first value.
      if (!resalePending) {
        const availableValues = uniqueValues.filter((v) => !soldOutValues.has(v));
        const preferredDefault = availableValues[0] ?? uniqueValues[0] ?? null;
        const currentVal = this.state.selectedOptions[optionIndex];
        const currentStillValid = currentVal
          && uniqueValues.includes(currentVal)
          && !soldOutValues.has(currentVal);
        if (!currentStillValid) {
          this.state.selectedOptions[optionIndex] = preferredDefault;
        }
      }

      this.querySelectorAll(`[data-option${optionIndex}-pills]`).forEach((container) => {
        container.innerHTML = '';
        if (!shouldRender) {
          container.closest('.baboodle-field')?.classList.add('baboodle-field--hidden');
          // Even if hidden, still set to the only available value
          if (uniqueValues.length === 1) {
            this.state.selectedOptions[optionIndex] = uniqueValues[0];
          }
          return;
        }
        container.closest('.baboodle-field')?.classList.remove('baboodle-field--hidden');
        uniqueValues.forEach((val) => {
          // Suppress the selected visual while resale availability is still
          // loading — nothing appears selected until we know what's in stock.
          const isSelected = !resalePending
            && val === this.state.selectedOptions[optionIndex];
          const pill = this._makePill(
            val,
            isSelected,
            () => this._onSelectOption(optionIndex, val)
          );
          pill.dataset['option' + optionIndex] = val;
          if (soldOutValues.has(val)) {
            pill.classList.add('baboodle-pill--disabled');
            pill.disabled = true;
            pill.title = 'Sold out';
          }
          container.appendChild(pill);
        });
      });
    }

    /**
     * Handle selecting a value for any option level.
     * Re-renders all subsequent option levels and resolves the variant.
     */
    _onSelectOption(optionIndex, value) {
      if (value === this.state.selectedOptions[optionIndex]) return;
      this.state.selectedOptions[optionIndex] = value;

      // Sync pills across all panels for this option level
      this.querySelectorAll(`[data-option${optionIndex}-pills] .baboodle-pill`).forEach((pill) => {
        pill.classList.toggle('baboodle-pill--selected', pill.dataset['option' + optionIndex] === value);
      });

      // Re-render all subsequent option levels (they depend on this selection)
      for (let i = optionIndex + 1; i < this._optionCount; i++) {
        this._renderOptionPills(i);
      }

      // Resolve the new variant ID from all option selections
      this._resolveVariantFromOptions();
    }

    // ── Variant resolution from option selections ──────

    /**
     * Find the Shopify variant matching all selected options.
     * Updates selectedVariantId, product media gallery, availability, and pricing.
     */
    _resolveVariantFromOptions() {
      const variants = this.config.variants || [];

      const match = variants.find((v) => {
        for (let i = 0; i < this._optionCount; i++) {
          const key = 'option' + (i + 1);
          const selected = this.state.selectedOptions[i];
          const variantVal = i === 0 ? (v[key] || v.title) : v[key];
          if (variantVal !== selected) return false;
        }
        return true;
      });

      const newId = match ? match.id : null;
      if (newId && newId !== this.state.selectedVariantId) {
        this.state.selectedVariantId = newId;

        // Update product gallery to show this variant's featured image
        this._updateProductMedia(newId);

        // Re-evaluate which durations / conditions are available
        this._updateConditionAvailability();
        this._renderResaleConditionPills();
        this._updateDurationAvailability();
        this._updateEverything();
        this._fetchAvailability(newId);
      }
    }

    /**
     * Switches the product media gallery to the selected variant's featured image.
     * Uses the same setActiveMedia() API the theme's own variant picker uses.
     */
    _updateProductMedia(variantId) {
      const variant = this.config.variants.find((v) => v.id === variantId);
      if (!variant || !variant.featured_media_id) return;

      // The gallery element lives in the same section, identified by section ID
      const sectionId = this.dataset.sectionId;
      const mediaGallery = document.querySelector(
        `[id^="MediaGallery-${sectionId}"]`
      );

      if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(variant.featured_media_id);
      }
    }

    // ── Condition pills ──────────────────────────────────

    _renderConditionPills() {
      // Subscription / Calendar panels: New / Refurbished
      this.querySelectorAll('[data-condition-pills]').forEach((container) => {
        const panel = container.dataset.conditionPills;
        container.innerHTML = '';

        const conditions = panel === 'resale'
          ? this._getResaleConditions()
          : [{ value: 'new', label: 'New' }, { value: 'refurbished', label: 'Refurbished' }];

        conditions.forEach((cond) => {
          const pill = this._makePill(
            cond.label,
            cond.value === this.state.selectedCondition,
            () => this._onSelectCondition(cond.value)
          );
          pill.dataset.condition = cond.value;
          container.appendChild(pill);
        });
      });

      this._updateConditionAvailability();
    }

    _getResaleConditions() {
      // Try to read from resale metafield; fall back to A-D
      if (this.config.resale && this.config.resale.options) {
        const grades = new Set();
        this.config.resale.options.forEach((opt) => {
          if (opt.condition) grades.add(opt.condition);
          if (opt.condition_grade) grades.add(opt.condition_grade);
        });
        if (grades.size > 0) {
          return Array.from(grades).map((g) => ({ value: g, label: g }));
        }
      }
      return [
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
      ];
    }

    _onSelectCondition(value) {
      if (value === this.state.selectedCondition) return;
      this.state.selectedCondition = value;

      this.querySelectorAll('[data-condition-pills] .baboodle-pill').forEach((pill) => {
        pill.classList.toggle('baboodle-pill--selected', pill.dataset.condition === value);
      });

      this._updateDurationAvailability();
      this._updateEverything();
    }

    /**
     * Disable condition pills that have no matching pricing group for the current variant.
     */
    _updateConditionAvailability() {
      // For now we don't have strong heuristics to disable conditions because
      // the metafield structure doesn't clearly encode condition → pricing group mapping.
      // This can be refined once we have real metafield samples.
      // Placeholder: all conditions enabled.
    }

    // ── Resale condition pills ───────────────────────────
    //
    // Each resale option has a `name` (the condition grade, e.g. "A"/"B"/"C"/"D")
    // and a variant applicability rule. Pills are filtered to grades actually
    // offered for the current variant.

    /**
     * Return the condition grades offered for the current variant, sorted alphabetically.
     */
    _getResaleConditionsForCurrentVariant() {
      if (!this.config.resale || !this.config.resale.options) return [];
      const vid = this.state.selectedVariantId;
      const seen = new Set();
      const conditions = [];
      this.config.resale.options.forEach((opt) => {
        if (!opt.name || seen.has(opt.name)) return;
        if (!this._groupMatchesVariant(opt, vid)) return;
        seen.add(opt.name);
        conditions.push({ value: opt.name, label: opt.name });
      });
      conditions.sort((a, b) => a.label.localeCompare(b.label));
      return conditions;
    }

    _renderResaleConditionPills() {
      const container = this.querySelector('[data-condition-pills="resale"]');
      if (!container) return;
      container.innerHTML = '';

      const conditions = this._getResaleConditionsForCurrentVariant();
      const field = container.closest('.baboodle-field');

      if (conditions.length === 0) {
        field?.classList.add('baboodle-field--hidden');
        this.state.selectedResaleCondition = null;
        return;
      }
      field?.classList.remove('baboodle-field--hidden');

      conditions.forEach((cond) => {
        const pill = this._makePill(
          cond.label,
          cond.value === this.state.selectedResaleCondition,
          () => this._onSelectResaleCondition(cond.value)
        );
        pill.dataset.resaleCondition = cond.value;
        container.appendChild(pill);
      });

      // Apply any already-cached sold-out state and fill the initial selection
      this._applyResaleAvailability();

      // Kick off availability checks for uncached grades; the result will
      // re-apply sold-out styling once responses arrive.
      this._prefetchResaleAvailability();
    }

    _onSelectResaleCondition(value) {
      if (value === this.state.selectedResaleCondition) return;
      this.state.selectedResaleCondition = value;

      this.querySelectorAll('[data-condition-pills="resale"] .baboodle-pill').forEach((pill) => {
        pill.classList.toggle('baboodle-pill--selected', pill.dataset.resaleCondition === value);
      });

      this._updateEverything();
    }

    /**
     * Apply cached sold-out state to the rendered resale pills and ensure
     * `selectedResaleCondition` points at an available grade when possible.
     * Safe to call at any time — a grade with no cache entry yet is treated
     * as available (optimistic) until the intent response arrives.
     */
    _applyResaleAvailability() {
      const container = this.querySelector('[data-condition-pills="resale"]');
      if (!container) return;
      const vid = this.state.selectedVariantId;

      const pills = Array.from(container.querySelectorAll('.baboodle-pill'));
      if (!pills.length) return;

      pills.forEach((pill) => {
        const grade = pill.dataset.resaleCondition;
        const option = this._findResaleOptionForGrade(grade);
        if (!option) return;
        const count = this.resaleAvailabilityCache[`${vid}:${option.global_id}`];
        const soldOut = count === 0;
        pill.classList.toggle('baboodle-pill--sold-out', soldOut);
        pill.disabled = soldOut;
        pill.title = soldOut ? 'Sold out' : '';
      });

      // Defer grade auto-selection until availability has fully resolved,
      // for the same no-flicker reason as the variant pills above.
      if (this._isResaleAvailabilityPending()) {
        // Keep any existing selection visually in sync, but don't pick one.
        pills.forEach((p) => {
          p.classList.toggle(
            'baboodle-pill--selected',
            !!this.state.selectedResaleCondition
              && p.dataset.resaleCondition === this.state.selectedResaleCondition
          );
        });
        this._updateEverything();
        return;
      }

      // If the selected grade is sold out (or unset), switch to the first
      // available grade.
      const availablePill = pills.find((p) => !p.disabled);
      const selectedPill = pills.find(
        (p) => p.dataset.resaleCondition === this.state.selectedResaleCondition
      );
      const selectedSoldOut = selectedPill && selectedPill.disabled;
      if ((!selectedPill || selectedSoldOut) && availablePill) {
        this.state.selectedResaleCondition = availablePill.dataset.resaleCondition;
      }

      pills.forEach((p) => {
        p.classList.toggle(
          'baboodle-pill--selected',
          p.dataset.resaleCondition === this.state.selectedResaleCondition
        );
      });

      this._updateEverything();
    }

    /**
     * Find the resale option matching a given grade name for the current variant.
     */
    _findResaleOptionForGrade(grade) {
      if (!this.config.resale || !this.config.resale.options || !grade) return null;
      const vid = this.state.selectedVariantId;
      return this.config.resale.options.find(
        (o) => o.name === grade && this._groupMatchesVariant(o, vid)
      ) || null;
    }

    /**
     * For every (variant, resale option) combo, call the intent endpoint to
     * read `available_item_count`. Results populate `resaleAvailabilityCache`
     * so sold-out grades AND fully sold-out variants are disabled before the
     * user ever clicks anything.
     *
     * We fetch across all variants (not just the current one) so the variant
     * selector can disable variants whose entire resale offering is sold out
     * — same treatment subscription/calendar get via method applicability.
     *
     * Cached entries are skipped; intent calls that error are cached as `0`
     * (conservatively sold-out) so we don't retry endlessly on a broken option.
     */
    _prefetchResaleAvailability() {
      if (typeof BaboodleSupercycleAPI === 'undefined') return;
      if (!this.config.resale || !this.config.resale.options) return;

      const options = this.config.resale.options;
      const variants = this.config.variants || [];

      variants.forEach((variant) => {
        const vid = variant.id;
        options.forEach((option) => {
          if (!option.global_id) return;
          if (!this._groupMatchesVariant(option, vid)) return;

          const cacheKey = `${vid}:${option.global_id}`;
          if (cacheKey in this.resaleAvailabilityCache) return; // already loaded / in-flight

          // Mark as in-flight so concurrent renders don't re-fire the same call
          this.resaleAvailabilityCache[cacheKey] = undefined;

          BaboodleSupercycleAPI.createRentalIntent(vid, option.global_id, {
            delivery_method_type: 'shipping',
          })
            .then((intent) => {
              this.resaleAvailabilityCache[cacheKey] = Number(intent.available_item_count ?? 0);
            })
            .catch(() => {
              this.resaleAvailabilityCache[cacheKey] = 0;
            })
            .finally(() => {
              this._onResaleAvailabilityUpdated();
            });
        });
      });
    }

    /**
     * Called whenever a resale availability response lands. Updates grade-pill
     * sold-out state for the current variant, and — when the resale panel is
     * active — re-renders variant option pills so fully-sold-out variants
     * disappear from the selector.
     */
    _onResaleAvailabilityUpdated() {
      this._applyResaleAvailability();

      if (this.state.activePanel === 'resale') {
        for (let i = 0; i < this._optionCount; i++) {
          this._renderOptionPills(i);
        }
        this._resolveVariantFromOptions();
      }
    }

    /**
     * Is this variant fully sold out for resale?
     * True only when every applicable resale option's count is known AND is 0.
     * Unknown counts (still in flight) are treated optimistically — we don't
     * claim "sold out" until we've actually heard back.
     *
     * Returns false for variants with no applicable resale options (they're
     * filtered out upstream by `_getMethodApplicableVariantIds` — "sold out"
     * wouldn't be the right label).
     */
    _isVariantSoldOutForResale(variantId) {
      const options = (this.config.resale && this.config.resale.options) || [];
      const applicable = options.filter((o) => this._groupMatchesVariant(o, variantId));
      if (applicable.length === 0) return false;
      return applicable.every((o) => {
        const count = this.resaleAvailabilityCache[`${variantId}:${o.global_id}`];
        return count === 0;
      });
    }

    /**
     * Are we still waiting on any resale availability response?
     * True while at least one (variant, option) combo has a missing or
     * `undefined` (in-flight) cache entry. Used to defer variant/grade
     * auto-selection on the resale panel so the user doesn't see a pill
     * flicker from one value to another once responses land.
     */
    _isResaleAvailabilityPending() {
      if (!this.config.resale || !this.config.resale.options) return false;
      const options = this.config.resale.options;
      const variants = this.config.variants || [];
      for (const v of variants) {
        for (const o of options) {
          if (!o.global_id) continue;
          if (!this._groupMatchesVariant(o, v.id)) continue;
          const key = `${v.id}:${o.global_id}`;
          if (this.resaleAvailabilityCache[key] === undefined) return true;
        }
      }
      return false;
    }

    // ── Duration pills (subscription only) ───────────────

    /**
     * Build duration pills dynamically from the pricing groups
     * in the subscription_configuration metafield.
     * Uses new stacked layout: big number on top, "months" below.
     */
    _renderDurationPills() {
      const container = this.querySelector('[data-duration-pills]');
      if (!container) return;
      container.innerHTML = '';

      const groups = this.config.subscription?.pricing_groups || [];

      // Collect all unique duration values from the metafield
      const durationsSet = new Set();
      groups.forEach((g) => {
        if (g.minimum_term_info && typeof g.minimum_term_info.count === 'number') {
          durationsSet.add(g.minimum_term_info.count);
        }
      });

      // Sort ascending
      const durations = Array.from(durationsSet).sort((a, b) => a - b);

      if (durations.length === 0) return;

      const best = parseInt(this.config.bestPeriod, 10);

      // If our current selected duration doesn't exist in the set, pick the best or first
      if (!durations.includes(this.state.selectedDuration)) {
        this.state.selectedDuration = durations.includes(best) ? best : durations[0];
      }

      durations.forEach((d) => {
        const isSelected = d === this.state.selectedDuration;

        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'baboodle-duration-pill' + (isSelected ? ' baboodle-duration-pill--selected' : '');
        pill.dataset.duration = d;

        // "Best" badge is placed dynamically in _updateDurationAvailability

        // Number
        const num = document.createElement('span');
        num.className = 'baboodle-duration-pill__number';
        num.textContent = d;
        pill.appendChild(num);

        // Label
        const label = document.createElement('span');
        label.className = 'baboodle-duration-pill__label';
        label.textContent = d === 1 ? 'month' : 'months';
        pill.appendChild(label);

        pill.addEventListener('click', () => this._onSelectDuration(d));
        container.appendChild(pill);
      });

      this._updateDurationAvailability();
    }

    _onSelectDuration(d) {
      if (d === this.state.selectedDuration) return;
      this.state.selectedDuration = d;

      const container = this.querySelector('[data-duration-pills]');
      if (container) {
        container.querySelectorAll('.baboodle-duration-pill').forEach((pill) => {
          pill.classList.toggle('baboodle-duration-pill--selected', parseInt(pill.dataset.duration, 10) === d);
        });
      }

      this._updateEverything();
    }

    /**
     * Disable duration pills that have no matching pricing group
     * for the current variant + condition.
     */
    _updateDurationAvailability() {
      const container = this.querySelector('[data-duration-pills]');
      if (!container || !this.config.subscription) return;

      const variantId = this.state.selectedVariantId;
      const groups = this.config.subscription.pricing_groups || [];

      const pills = Array.from(container.querySelectorAll('.baboodle-duration-pill'));
      pills.forEach((pill) => {
        const d = parseInt(pill.dataset.duration, 10);
        const hasMatch = groups.some(
          (g) => this._groupMatchesVariant(g, variantId) && this._groupMatchesDuration(g, d)
        );

        pill.classList.toggle('baboodle-duration-pill--disabled', !hasMatch);
        pill.disabled = !hasMatch;
      });

      // Move "Best" badge: if configured best is disabled, use next longest available
      const best = parseInt(this.config.bestPeriod, 10);
      // Remove any existing badge
      container.querySelectorAll('.baboodle-badge--best').forEach((b) => b.remove());
      // Find available pills sorted by duration descending
      const availablePills = pills
        .filter((p) => !p.disabled)
        .sort((a, b) => parseInt(b.dataset.duration, 10) - parseInt(a.dataset.duration, 10));
      // Prefer the configured best if available, otherwise next longest
      let bestPill = availablePills.find((p) => parseInt(p.dataset.duration, 10) === best);
      if (!bestPill && availablePills.length) {
        bestPill = availablePills[0];
      }
      if (bestPill) {
        const badge = document.createElement('span');
        badge.className = 'baboodle-badge baboodle-badge--best';
        badge.textContent = 'Best';
        bestPill.prepend(badge);
      }

      // If current selection became unavailable, auto-select nearest available
      const currentPill = container.querySelector(`.baboodle-duration-pill[data-duration="${this.state.selectedDuration}"]`);
      if (currentPill && currentPill.disabled) {
        const available = container.querySelector('.baboodle-duration-pill:not(.baboodle-duration-pill--disabled)');
        if (available) {
          this._onSelectDuration(parseInt(available.dataset.duration, 10));
        }
      }
    }

    // ── Pill factory ─────────────────────────────────────

    _makePill(label, selected, onClick) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'baboodle-pill' + (selected ? ' baboodle-pill--selected' : '');
      pill.textContent = label;
      pill.addEventListener('click', onClick);
      return pill;
    }

    // ═══════════════════════════════════════════════════════
    //  Pricing group helpers
    // ═══════════════════════════════════════════════════════

    /**
     * Check if a subscription pricing group applies to a given variant.
     *
     * The metafield stores variant IDs as a mix of numbers and strings,
     * so we normalise both sides to string for comparison.
     * Groups can also have a blacklisted_variant_shopify_ids deny-list.
     */
    _groupMatchesVariant(group, variantId) {
      const vid = String(variantId);

      // Check blacklist first — if the variant is blacklisted, it never matches
      if (group.blacklisted_variant_shopify_ids && group.blacklisted_variant_shopify_ids.length) {
        const isBlacklisted = group.blacklisted_variant_shopify_ids.some((id) => String(id) === vid);
        if (isBlacklisted) return false;
      }

      // "all" means every non-blacklisted variant
      if (group.variant_application_type === 'all') return true;

      // "some" / explicit list — normalise every ID to string for comparison
      if (!group.variant_application_shopify_ids || !group.variant_application_shopify_ids.length) return false;
      return group.variant_application_shopify_ids.some((id) => String(id) === vid);
    }

    /**
     * Check if a subscription pricing group matches a given duration (months).
     */
    _groupMatchesDuration(group, durationMonths) {
      if (!group.minimum_term_info) return false;
      return group.minimum_term_info.count === durationMonths;
    }

    /**
     * Return the set of variant IDs that are applicable to the current rental method.
     * For subscription: a variant is applicable if ANY pricing group includes it.
     * For calendar: a variant is applicable if ANY rental period includes it.
     * For resale / null: all variants are applicable (no filtering).
     *
     * Returns null when no filtering should occur (all variants allowed).
     */
    _getMethodApplicableVariantIds() {
      const panel = this.state.activePanel;
      let groups = [];

      if (panel === 'subscription' && this.config.subscription?.pricing_groups) {
        groups = this.config.subscription.pricing_groups;
      } else if (panel === 'calendar' && this.config.calendar?.rental_periods) {
        groups = this.config.calendar.rental_periods;
      } else if (panel === 'resale' && this.config.resale?.options) {
        groups = this.config.resale.options;
      } else {
        return null; // no filtering
      }

      // Collect every variant ID accepted by at least one group/period.
      // Sold-out filtering for resale is NOT done here — sold-out variants
      // still render so the customer can see them, just disabled. See
      // `_renderOptionPills` for the per-pill sold-out treatment.
      const ids = new Set();
      const allVariants = this.config.variants || [];
      for (const v of allVariants) {
        if (groups.some((g) => this._groupMatchesVariant(g, v.id))) {
          ids.add(String(v.id));
        }
      }
      return ids;
    }

    /**
     * Find the matching subscription pricing group for current selections.
     * Returns the group object or null.
     */
    _findSubscriptionGroup() {
      if (!this.config.subscription || !this.config.subscription.pricing_groups) return null;

      const vid = this.state.selectedVariantId;
      const dur = this.state.selectedDuration;

      return this.config.subscription.pricing_groups.find(
        (g) => this._groupMatchesVariant(g, vid) && this._groupMatchesDuration(g, dur)
      ) || null;
    }

    /**
     * Find the matching calendar rental period for the selected date range.
     * Returns the period object or null.
     */
    _findCalendarPeriod() {
      if (!this.config.calendar || !this.config.calendar.rental_periods) return null;
      if (!this.state.deliveryDate || !this.state.returnDate) return null;

      const days = this._inclusiveDayCount(this.state.deliveryDate, this.state.returnDate);

      // Try exact match first
      let period = this.config.calendar.rental_periods.find((p) => p.duration_count === days);
      if (period) return period;

      // Fall back to nearest period that covers the range (>=)
      let closest = null;
      let closestDiff = Infinity;
      for (const p of this.config.calendar.rental_periods) {
        if (p.duration_count >= days) {
          const diff = p.duration_count - days;
          if (diff < closestDiff) {
            closestDiff = diff;
            closest = p;
          }
        }
      }
      // If no period >= days, use the largest available period
      if (!closest) {
        closest = this.config.calendar.rental_periods.reduce((max, p) =>
          (p.duration_count > (max?.duration_count || 0)) ? p : max, null
        );
      }
      return closest;
    }

    /**
     * Find the matching resale option for the current variant + condition grade.
     * Returns the option object or null. Matches option.name (the grade identifier
     * used in the Supercycle metafield) and requires variant applicability.
     */
    _findResaleOption() {
      if (!this.config.resale || !this.config.resale.options) return null;
      const vid = this.state.selectedVariantId;
      const cond = this.state.selectedResaleCondition;
      if (!cond) return null;

      return this.config.resale.options.find(
        (o) => o.name === cond && this._groupMatchesVariant(o, vid)
      ) || null;
    }

    // ═══════════════════════════════════════════════════════
    //  Price calculation
    // ═══════════════════════════════════════════════════════

    _updateEverything() {
      this._updatePriceDisplay();
      this._updatePriceHints();
      this._updateButtonStates();
    }

    _updatePriceDisplay() {
      ['subscription', 'calendar', 'resale'].forEach((method) => {
        const display = this.querySelector(`[data-price-display="${method}"]`);
        if (!display) return;
        const amountEl = display.querySelector('.baboodle-price__amount');
        const periodEl = display.querySelector('.baboodle-price__period');
        if (!amountEl) return;

        const price = this._getPrice(method);
        if (price !== null) {
          amountEl.textContent = this.config.currencySymbol + this._fmt(price);
        } else {
          amountEl.textContent = this.config.currencySymbol + '--';
        }

        if (periodEl) {
          if (method === 'subscription') {
            periodEl.textContent = '/ month';
          } else if (method === 'calendar') {
            periodEl.textContent = price !== null ? 'total' : '';
          } else {
            periodEl.textContent = '';
          }
        }
      });
    }

    _updatePriceHints() {
      ['subscription', 'calendar', 'resale'].forEach((method) => {
        const hint = this.querySelector(`[data-price-hint="${method}"]`);
        if (!hint) return;

        const minPrice = this._getMinPrice(method);
        const sym = this.config.currencySymbol;

        if (minPrice !== null) {
          if (method === 'subscription') {
            hint.textContent = `From ${sym}${this._fmt(minPrice)}/mo`;
          } else if (method === 'calendar') {
            hint.textContent = `From ${sym}${this._fmt(minPrice)}`;
          } else {
            hint.textContent = `From ${sym}${this._fmt(minPrice)}`;
          }
        }
      });
    }

    /**
     * Enable/disable ATC buttons based on current selection validity.
     */
    _updateButtonStates() {
      this.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
        if (this._submitting) return; // don't interfere while submitting
        const method = btn.dataset.addToCart;
        const ready = this._isReadyToAdd(method);
        btn.disabled = !ready;
      });
    }

    /**
     * Check whether the current state is complete enough to add to cart.
     */
    _isReadyToAdd(method) {
      if (!this.state.selectedVariantId) return false;

      if (method === 'subscription') {
        return !!this.state.deliveryDate && !!this._findSubscriptionGroup();
      }

      if (method === 'calendar') {
        return !!this.state.deliveryDate && !!this.state.returnDate;
      }

      if (method === 'resale') {
        // Resale needs a variant + a condition grade resolving to a real option,
        // and (once availability has loaded) at least one item in stock.
        const option = this._findResaleOption();
        if (!this.state.selectedResaleCondition || !option) return false;
        const count = this.resaleAvailabilityCache[`${this.state.selectedVariantId}:${option.global_id}`];
        if (count === 0) return false;
        return true;
      }

      return false;
    }

    /**
     * Get the price for the currently selected combination in a method.
     * Returns price in CENTS (integer) or null.
     */
    _getPrice(method) {
      if (method === 'subscription') {
        const group = this._findSubscriptionGroup();
        if (!group) return null;
        // Try various field names Supercycle might use
        return group.initial_price_cents
          ?? group.price_cents
          ?? group.price
          ?? null;
      }

      if (method === 'calendar') {
        const period = this._findCalendarPeriod();
        if (!period) return null;
        return period.total_price
          ?? period.price_cents
          ?? period.price
          ?? null;
      }

      if (method === 'resale') {
        const option = this._findResaleOption();
        if (!option) return null;
        return option.checkout_price_cents
          ?? option.price_cents
          ?? option.price
          ?? null;
      }

      return null;
    }

    /**
     * Get the minimum possible price across all options for a method.
     * Used for the accordion header "From £XX" hints.
     */
    _getMinPrice(method) {
      let prices = [];

      if (method === 'subscription' && this.config.subscription?.pricing_groups) {
        prices = this.config.subscription.pricing_groups
          .map((g) => g.initial_price_cents ?? g.price_cents ?? g.price)
          .filter((p) => p != null);
      }

      if (method === 'calendar' && this.config.calendar?.rental_periods) {
        prices = this.config.calendar.rental_periods
          .map((p) => p.total_price ?? p.price_cents ?? p.price)
          .filter((p) => p != null);
      }

      if (method === 'resale' && this.config.resale?.options) {
        prices = this.config.resale.options
          .map((o) => o.checkout_price_cents ?? o.price_cents ?? o.price)
          .filter((p) => p != null);
      }

      return prices.length > 0 ? Math.min(...prices) : null;
    }

    /**
     * Format price from cents → "29.99" or "34" (drop .00)
     */
    _fmt(cents) {
      const amount = cents / 100;
      return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    }

    // ═══════════════════════════════════════════════════════
    //  Date pickers (flatpickr)
    // ═══════════════════════════════════════════════════════

    _initDatePickers() {
      if (typeof flatpickr === 'undefined') {
        // Deferred script not loaded yet — retry shortly
        let attempts = 0;
        const retry = () => {
          attempts++;
          if (typeof flatpickr !== 'undefined') {
            this._createDatePickers();
          } else if (attempts < 50) {
            setTimeout(retry, 200);
          } else {
            console.warn('[BaboodlePDP] flatpickr not loaded after 10s');
          }
        };
        setTimeout(retry, 100);
        return;
      }
      this._createDatePickers();
    }

    _createDatePickers() {
      this._createSubscriptionPicker();
      this._createCalendarPicker();
    }

    _createSubscriptionPicker() {
      const input = this.querySelector('[data-datepicker="subscription"]');
      if (!input || typeof flatpickr === 'undefined') return;

      const minDate = this._minSelectableDate();

      this.pickers.subscription = flatpickr(input, {
        mode: 'single',
        dateFormat: 'd/m/Y',
        minDate: minDate,
        disable: this._buildDeliveryDisableList(),
        locale: { firstDayOfWeek: 1 },
        onChange: (dates) => {
          if (dates.length) {
            this.state.deliveryDate = dates[0];
            this._clearError('subscription-date');
            this._updateEverything();
          }
        },
        onReady: (_dates, _str, inst) => { this._updateMonthLabel(inst); },
        onMonthChange: (_dates, _str, inst) => { this._updateMonthLabel(inst); },
        onYearChange: (_dates, _str, inst) => { this._updateMonthLabel(inst); },
        onOpen: () => {
          this._ensureViewableDeliveryMonth(this.pickers.subscription);
          this._showCalendarBackdrop(this.pickers.subscription);
        },
        onClose: () => {
          this._removeCalendarBackdrop();
          input.blur();
        },
      });
    }

    _createCalendarPicker() {
      const hiddenInput = this.querySelector('[data-datepicker="calendar"]');
      if (!hiddenInput || typeof flatpickr === 'undefined') return;

      // Get the display inputs
      const deliveryDisplay = this.querySelector('[data-calendar-delivery-display]');
      const returnDisplay = this.querySelector('[data-calendar-return-display]');

      const minDate = this._minSelectableDate();

      // Phase: 'delivery' = choosing start date, 'return' = choosing end date.
      // IMPORTANT — flatpickr range mode treats disabled dates as impassable
      // barriers: you cannot select an end date beyond ANY disabled date.
      // So day-of-week rules (e.g. "no Sundays") MUST NOT go in the disable
      // list, otherwise the first disabled Sunday blocks every date after it.
      //
      // Instead:
      //  • delivery day-of-week → only in disable during 'delivery' phase
      //    (removed on phase switch so it can't block end-date selection)
      //  • return day-of-week  → never in disable; enforced via onDayCreate
      //    (pointer-events:none CSS) + onChange validation
      this._calendarDisablePhase = 'delivery';

      this.pickers.calendar = flatpickr(hiddenInput, {
        mode: 'range',
        dateFormat: 'd/m/Y',
        minDate: minDate,
        disable: [
          (date) => this._isDateDisabledForCurrentPhase(date),
        ],
        locale: { firstDayOfWeek: 1 },
        positionElement: deliveryDisplay,
        onReady: (_dates, _str, inst) => { this._updateMonthLabel(inst); },
        onMonthChange: (_dates, _str, inst) => { this._updateMonthLabel(inst); },
        onYearChange: (_dates, _str, inst) => { this._updateMonthLabel(inst); },

        // Tag each day cell with CSS classes for day-of-week restrictions.
        // These DON'T use flatpickr's disable mechanism, avoiding the
        // barrier problem while still giving clear visual feedback.
        onDayCreate: (dObj, dStr, fp, dayElem) => {
          this._styleCalendarDay(dayElem);
        },

        onChange: (dates) => {
          if (dates.length === 2) {
            const [start, end] = dates;

            // Same-day selection is not valid — minimum rental is 2 inclusive days
            if (this._inclusiveDayCount(start, end) < 2) {
              this.pickers.calendar.clear();
              this.pickers.calendar.setDate([start], true); // keep start, re-enter return phase
              this.state.deliveryDate = start;
              this.state.returnDate = null;
              if (deliveryDisplay) deliveryDisplay.value = this._formatDisplay(start);
              if (returnDisplay) returnDisplay.value = '';
              this._showError('calendar-date', 'Please select a different return date');
              this._switchCalendarPhase('return');
              return;
            }

            // Validate return date against return day-of-week + specific rules
            if (this._isReturnDisabled(end)) {
              this.pickers.calendar.clear();
              this._showError('calendar-date', 'The selected return date is not available');
              if (deliveryDisplay) deliveryDisplay.value = '';
              if (returnDisplay) returnDisplay.value = '';
              this._switchCalendarPhase('delivery');
              return;
            }

            // Validate range length — max matches the largest rental_period.duration_count
            const diff = this._inclusiveDayCount(start, end);
            if (diff > 30) {
              this.pickers.calendar.clear();
              this._showError('calendar-range');
              if (deliveryDisplay) deliveryDisplay.value = '';
              if (returnDisplay) returnDisplay.value = '';
              this._switchCalendarPhase('delivery');
              return;
            }

            this.state.deliveryDate = start;
            this.state.returnDate = end;

            // Update display fields
            if (deliveryDisplay) deliveryDisplay.value = this._formatDisplay(start);
            if (returnDisplay) returnDisplay.value = this._formatDisplay(end);

            this._clearError('calendar-date');
            this._clearError('calendar-range');
            this._updateEverything();

            // Range complete — switch back to delivery rules
            this._switchCalendarPhase('delivery');
          } else if (dates.length === 1) {
            this.state.deliveryDate = dates[0];
            this.state.returnDate = null;

            if (deliveryDisplay) deliveryDisplay.value = this._formatDisplay(dates[0]);
            if (returnDisplay) returnDisplay.value = '';

            this._updateEverything();

            // Start date picked — drop day-of-week from disable list and
            // switch visual indicators to show return-restricted days.
            this._switchCalendarPhase('return');
          }
        },
        onOpen: () => {
          if (!this.pickers.calendar.selectedDates.length) {
            this._switchCalendarPhase('delivery');
          }
          this._ensureViewableDeliveryMonth(this.pickers.calendar);
          this._showCalendarBackdrop(this.pickers.calendar);
        },
        onClose: () => {
          this._removeCalendarBackdrop();
          if (deliveryDisplay) deliveryDisplay.blur();
          if (returnDisplay) returnDisplay.blur();
          hiddenInput.blur();
          if (document.activeElement) document.activeElement.blur();
        },
      });

      // Make both display fields open the calendar
      if (deliveryDisplay) {
        deliveryDisplay.addEventListener('click', () => {
          this.pickers.calendar?.open();
        });
      }
      if (returnDisplay) {
        returnDisplay.addEventListener('click', () => {
          this.pickers.calendar?.open();
        });
      }
    }

    /**
     * Format a date for display in the date fields: "15/03/2026"
     */
    _formatDisplay(date) {
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
    }

    // ── Disable-list builders ────────────────────────────
    //
    // Both pickers feed flatpickr a single function in the `disable` array
    // rather than a mix of strings/range objects. Flatpickr normalises any
    // string or {from,to} entries by running them through `parseDate` with
    // the picker's `dateFormat` ('d/m/Y'), which silently mis-parses our
    // ISO 'YYYY-MM-DD' config values (e.g. "2026-04-28" → day=2026). A
    // function-based disable list sidesteps that entirely.

    _buildDeliveryDisableList() {
      return [(date) => this._isDeliveryDateDisabled(date)];
    }

    _buildReturnDisableList() {
      return [(date) => this._isReturnDateDisabled(date)];
    }

    /**
     * True if `date` matches any delivery restriction (day-of-week, range,
     * specific date, or API-fetched unavailability). Used by the
     * subscription single-date picker and by the calendar picker during
     * its 'delivery' phase.
     */
    _isDeliveryDateDisabled(date) {
      const days = this.config.disabledDeliveryDays || [];
      if (days.includes(date.getDay())) return true;

      const isoStr = this._isoDate(date);
      for (const r of (this.config.disabledDeliveryRanges || [])) {
        if (isoStr >= r.from && isoStr <= r.to) return true;
      }
      if ((this.config.disabledDeliveryDates || []).includes(isoStr)) return true;
      if (this._getApiUnavailableDates().includes(isoStr)) return true;

      return false;
    }

    /**
     * True if `date` matches any return restriction. Mirror of the
     * delivery check, against the return-side config.
     */
    _isReturnDateDisabled(date) {
      const days = this.config.disabledReturnDays || [];
      if (days.includes(date.getDay())) return true;

      const isoStr = this._isoDate(date);
      for (const r of (this.config.disabledReturnRanges || [])) {
        if (isoStr >= r.from && isoStr <= r.to) return true;
      }
      if ((this.config.disabledReturnDates || []).includes(isoStr)) return true;
      if (this._getApiUnavailableDates().includes(isoStr)) return true;

      return false;
    }

    /**
     * If `picker`'s currently-displayed month contains zero selectable
     * delivery dates (after minDate + day-of-week + ranges + specific
     * dates + API unavailability), jump forward to the first month that
     * has at least one selectable date. Skipped if the user already has
     * a date selected — we don't want to override their navigation.
     *
     * Called from each picker's `onOpen` so it re-evaluates on every
     * open (covering both initial display and post-API-load reopens).
     */
    _ensureViewableDeliveryMonth(picker) {
      if (!picker || picker.selectedDates.length) return;

      const minDate = this._minSelectableDate();
      const viewStart = new Date(picker.currentYear, picker.currentMonth, 1);
      const cursor = viewStart > minDate ? viewStart : new Date(minDate);

      // Bounded scan to avoid pathological loops if every date is blocked.
      for (let i = 0; i < 366; i++) {
        if (!this._isDeliveryDateDisabled(cursor)) {
          if (cursor.getFullYear() !== picker.currentYear || cursor.getMonth() !== picker.currentMonth) {
            picker.jumpToDate(cursor, false);
            // jumpToDate doesn't fire onMonthChange, so refresh the
            // custom month label manually.
            this._updateMonthLabel(picker);
          }
          return;
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    /**
     * Disable check for the calendar flatpickr.
     *
     * Day-of-week rules are ONLY applied in 'delivery' phase (choosing the
     * start date). In 'return' phase they are removed so they don't act as
     * impassable barriers — flatpickr range mode won't let you select an
     * end date that's beyond ANY disabled date. Return day-of-week is
     * instead enforced via onDayCreate CSS (pointer-events:none) + onChange.
     *
     * Specific date strings, date ranges, and API-unavailable dates remain
     * hard-disabled in both phases (those represent real inventory blocks).
     */
    _isDateDisabledForCurrentPhase(date) {
      const phase = this._calendarDisablePhase || 'delivery';

      if (phase === 'delivery') {
        return this._isDeliveryDateDisabled(date);
      }

      // 'return' phase — skip day-of-week (handled via onDayCreate CSS),
      // but keep specific dates / ranges / API blocks as hard barriers.
      const isoStr = this._isoDate(date);
      for (const r of (this.config.disabledDeliveryRanges || [])) {
        if (isoStr >= r.from && isoStr <= r.to) return true;
      }
      if ((this.config.disabledDeliveryDates || []).includes(isoStr)) return true;
      if (this._getApiUnavailableDates().includes(isoStr)) return true;

      return false;
    }

    /**
     * Tag a calendar day element with CSS classes for return-phase
     * restrictions. In 'delivery' phase, delivery-restricted days are
     * already hard-disabled by flatpickr. In 'return' phase we use a
     * CSS class (pointer-events:none) to block clicks WITHOUT creating
     * flatpickr barriers that would prevent range selection.
     *
     * Covers all three return rules: day-of-week, date ranges, and
     * specific dates — so every invalid return cell is visually blocked
     * before the user clicks, rather than rejected post-hoc.
     */
    _styleCalendarDay(dayElem) {
      dayElem.classList.remove('baboodle-day-no-return');
      if (this._calendarDisablePhase !== 'return') return;

      if (this._isReturnDisabled(dayElem.dateObj)) {
        dayElem.classList.add('baboodle-day-no-return');
      }
    }

    /**
     * Switch between delivery/return phase. Calls redraw() which is safe
     * (does NOT re-trigger onChange, unlike set('disable',…)).
     */
    _switchCalendarPhase(phase) {
      if (!this.pickers.calendar || this._calendarDisablePhase === phase) return;
      this._calendarDisablePhase = phase;
      this.pickers.calendar.redraw();
    }

    // ── Mobile bottom-sheet backdrop ──────────────────────
    _isMobile() {
      return window.innerWidth <= 767;
    }

    _showCalendarBackdrop(pickerInstance) {
      if (!this._isMobile()) return;
      this._removeCalendarBackdrop();
      const backdrop = document.createElement('div');
      backdrop.className = 'baboodle-flatpickr-backdrop';
      backdrop.addEventListener('click', () => {
        if (pickerInstance) pickerInstance.close();
      });
      document.body.appendChild(backdrop);
      this._calendarBackdrop = backdrop;
    }

    _removeCalendarBackdrop() {
      if (this._calendarBackdrop) {
        this._calendarBackdrop.remove();
        this._calendarBackdrop = null;
      }
    }

    // ── Plain-text month/year label for flatpickr ─────────
    static _MONTH_NAMES = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];

    _updateMonthLabel(instance) {
      const container = instance.calendarContainer?.querySelector('.flatpickr-current-month');
      if (!container) return;
      let label = container.querySelector('.baboodle-month-label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'baboodle-month-label';
        container.appendChild(label);
      }
      const m = instance.currentMonth;
      const y = instance.currentYear;
      label.textContent = `${BaboodlePdpBlock._MONTH_NAMES[m]} ${y}`;
    }

    _isReturnDisabled(date) {
      const days = this.config.disabledReturnDays || [];
      if (days.includes(date.getDay())) return true;

      const isoStr = this._isoDate(date);
      for (const r of (this.config.disabledReturnRanges || [])) {
        if (isoStr >= r.from && isoStr <= r.to) return true;
      }
      if ((this.config.disabledReturnDates || []).includes(isoStr)) return true;

      return false;
    }

    _refreshDatePickers() {
      if (this.pickers.subscription) {
        this.pickers.subscription.set('disable', this._buildDeliveryDisableList());
      }
      if (this.pickers.calendar) {
        // The calendar uses a dynamic disable function that reads the
        // current phase automatically, so a plain redraw() is sufficient.
        this.pickers.calendar.redraw();
      }
    }

    // ═══════════════════════════════════════════════════════
    //  Availability API
    // ═══════════════════════════════════════════════════════

    async _fetchAvailability(variantId) {
      if (!variantId || typeof BaboodleSupercycleAPI === 'undefined') return;
      if (this.availabilityCache[variantId]) {
        this._refreshDatePickers();
        return;
      }

      try {
        const data = await BaboodleSupercycleAPI.getAvailabilityTimeline(variantId);
        this.availabilityCache[variantId] = data;
        this._refreshDatePickers();
      } catch (err) {
        console.warn('[BaboodlePDP] Availability fetch failed:', err);
      }
    }

    _getApiUnavailableDates() {
      const data = this.availabilityCache[this.state.selectedVariantId];
      if (!data || !data.occupancy) return [];
      return Object.entries(data.occupancy)
        .filter(([, count]) => count === 0)
        .map(([dateStr]) => dateStr);
    }

    // ═══════════════════════════════════════════════════════
    //  Add to Cart
    // ═══════════════════════════════════════════════════════

    _initAddToCart() {
      this.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
        btn.addEventListener('click', () => this._handleAddToCart(btn.dataset.addToCart));
      });
    }

    async _handleAddToCart(method) {
      if (this._submitting) return;
      if (!this._validate(method)) return;

      const btn = this.querySelector(`[data-add-to-cart="${method}"]`);
      this._submitting = true;
      this._setButtonLoading(btn, true);
      this._clearInlineError(method);

      try {
        // 1. Resolve the Supercycle global_id for the selected option
        const globalId = this._resolveGlobalId(method);
        if (!globalId) {
          throw new Error('Unable to resolve rental option. Please try different selections.');
        }

        // 2. Build intent params
        const intentParams = { delivery_method_type: 'shipping' };
        if (method !== 'resale' && this.state.deliveryDate) {
          intentParams.rental_start = this._isoDate(this.state.deliveryDate);
        }

        // 3. Call Supercycle Intent API
        const intent = await BaboodleSupercycleAPI.createRentalIntent(
          this.state.selectedVariantId,
          globalId,
          intentParams
        );

        // 4. Build cart form data — spread intent attributes
        const formData = new FormData();
        formData.append('id', this.state.selectedVariantId);
        formData.append('quantity', 1);

        if (intent.attributes) {
          for (const [key, value] of Object.entries(intent.attributes)) {
            if (value != null) formData.append(key, String(value));
          }
        }

        // Ask Shopify to render the cart drawer / cart icon sections in the
        // response so the theme's ON_ITEM_ADDED handler can refresh them.
        this._appendCartSections(formData);

        // 5. Submit to Shopify cart
        const cartResponse = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          body: formData,
        });

        if (!cartResponse.ok) {
          const cartError = await cartResponse.json().catch(() => null);
          throw new Error(cartError?.description || `Cart error: ${cartResponse.status}`);
        }

        const result = await cartResponse.json();

        // 6. Trigger theme cart drawer / notification
        if (window.FoxThemeEvents) {
          window.FoxThemeEvents.emit('ON_ITEM_ADDED', result);
        }
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: result }));

        // 7. Handle add-ons if the intent returned any
        if (intent.add_ons && intent.add_ons.length) {
          for (const addon of intent.add_ons) {
            if (addon.attributes) {
              const addonForm = new FormData();
              for (const [k, v] of Object.entries(addon.attributes)) {
                if (v != null) addonForm.append(k, String(v));
              }
              this._appendCartSections(addonForm);
              const addonResponse = await fetch(this.config.cartAddUrl, { method: 'POST', body: addonForm });
              if (addonResponse.ok && window.FoxThemeEvents) {
                const addonResult = await addonResponse.json().catch(() => null);
                if (addonResult) window.FoxThemeEvents.emit('ON_ITEM_ADDED', addonResult);
              }
            }
          }
        }

        // 8. Success — brief visual feedback
        this._setButtonSuccess(btn);

      } catch (err) {
        console.error('[BaboodlePDP] Add to cart failed:', err);
        this._showInlineError(method, err.message || 'Something went wrong. Please try again.');
      } finally {
        this._submitting = false;
        // Button state is restored by _setButtonSuccess or after error timeout
        setTimeout(() => {
          this._setButtonLoading(btn, false);
          this._updateButtonStates();
        }, 2000);
      }
    }

    /**
     * Append Shopify Section Rendering API params to a cart-add FormData so
     * the response body includes rendered HTML for the theme's cart drawer
     * (and main cart on the cart page). Mirrors product-form.js so the
     * ON_ITEM_ADDED subscribers can re-render from `response.sections`.
     */
    _appendCartSections(formData) {
      const sectionIds = [];
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((s) => {
          if (s && s.id) sectionIds.push(s.id);
        });
      }
      const mainCart = document.querySelector('main-cart');
      if (mainCart && typeof mainCart.getSectionsToRender === 'function') {
        mainCart.getSectionsToRender().forEach((s) => {
          if (s && s.section) sectionIds.push(s.section);
        });
      }
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }
    }

    /**
     * Resolve the Supercycle global_id for the current selections in a method.
     */
    _resolveGlobalId(method) {
      if (method === 'subscription') {
        const group = this._findSubscriptionGroup();
        return group?.global_id || null;
      }
      if (method === 'calendar') {
        const period = this._findCalendarPeriod();
        return period?.global_id || null;
      }
      if (method === 'resale') {
        const option = this._findResaleOption();
        return option?.global_id || null;
      }
      return null;
    }

    _validate(method) {
      let valid = true;

      if (!this.state.selectedVariantId) {
        valid = false;
      }

      if (method === 'subscription' && !this.state.deliveryDate) {
        this._showError('subscription-date');
        valid = false;
      }

      if (method === 'calendar') {
        if (!this.state.deliveryDate || !this.state.returnDate) {
          this._showError('calendar-date');
          valid = false;
        }
      }

      // Resale doesn't need date validation

      return valid;
    }

    _setButtonLoading(btn, loading) {
      if (!btn) return;
      if (loading) {
        btn.disabled = true;
        btn.dataset.originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.classList.add('baboodle-atc--loading');
      } else {
        btn.classList.remove('baboodle-atc--loading');
        btn.classList.remove('baboodle-atc--success');
        btn.textContent = btn.dataset.originalText || 'Add to Cart';
      }
    }

    _setButtonSuccess(btn) {
      if (!btn) return;
      btn.textContent = '\u2713 Added';
      btn.classList.remove('baboodle-atc--loading');
      btn.classList.add('baboodle-atc--success');
    }

    // ═══════════════════════════════════════════════════════
    //  Variant change listener (from theme variant picker)
    // ═══════════════════════════════════════════════════════

    _subscribeToVariantChanges() {
      if (typeof window.FoxThemeEvents !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantUnsub = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.variantChange,
          (evt) => {
            if (evt.data?.variant) {
              this._onExternalVariantChange(evt.data.variant);
            }
          }
        );
      }
    }

    /**
     * Handle variant changes from the theme's own variant picker.
     * Maps the incoming variant to our selectedOptions state and syncs pills.
     */
    _onExternalVariantChange(variant) {
      const match = this.config.variants.find((v) => v.id === variant.id);
      if (!match) return;

      let changed = false;

      for (let i = 0; i < this._optionCount; i++) {
        const key = 'option' + (i + 1);
        const newVal = i === 0 ? (match[key] || match.title) : match[key];

        if (newVal !== this.state.selectedOptions[i]) {
          this.state.selectedOptions[i] = newVal;
          this.querySelectorAll(`[data-option${i}-pills] .baboodle-pill`).forEach((pill) => {
            pill.classList.toggle('baboodle-pill--selected', pill.dataset['option' + i] === newVal);
          });
          // Re-render all subsequent option levels
          for (let j = i + 1; j < this._optionCount; j++) {
            this._renderOptionPills(j);
          }
          changed = true;
        }
      }

      if (changed || match.id !== this.state.selectedVariantId) {
        this.state.selectedVariantId = match.id;
        this._updateConditionAvailability();
        this._renderResaleConditionPills();
        this._updateDurationAvailability();
        this._updateEverything();
        this._fetchAvailability(match.id);
      }
    }

    // ═══════════════════════════════════════════════════════
    //  Error helpers
    // ═══════════════════════════════════════════════════════

    _showError(key, msg) {
      const el = this.querySelector(`[data-error="${key}"]`);
      if (el) {
        if (msg) el.textContent = msg;
        el.hidden = false;
      }
    }

    _clearError(key) {
      const el = this.querySelector(`[data-error="${key}"]`);
      if (el) el.hidden = true;
    }

    _showInlineError(method, msg) {
      // Create or update an error element near the ATC button
      const btn = this.querySelector(`[data-add-to-cart="${method}"]`);
      if (!btn) return;

      const actionRow = btn.closest('.baboodle-action-row') || btn.parentElement;
      let errorEl = actionRow.parentElement.querySelector('.baboodle-atc-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'baboodle-atc-error baboodle-field__error';
        actionRow.parentElement.insertBefore(errorEl, actionRow.nextSibling);
      }
      errorEl.textContent = msg;
      errorEl.hidden = false;

      // Auto-clear after 8 seconds
      setTimeout(() => {
        if (errorEl) errorEl.hidden = true;
      }, 8000);
    }

    _clearInlineError(method) {
      const btn = this.querySelector(`[data-add-to-cart="${method}"]`);
      if (!btn) return;
      const actionRow = btn.closest('.baboodle-action-row') || btn.parentElement;
      const errorEl = actionRow.parentElement.querySelector('.baboodle-atc-error');
      if (errorEl) errorEl.hidden = true;
    }

    // ═══════════════════════════════════════════════════════
    //  Utilities
    // ═══════════════════════════════════════════════════════

    _isoDate(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    // Inclusive day count: 3rd → 18th = 16 days. Supercycle's rental_periods
    // duration_count and selling-plan length are both inclusive-of-both-endpoints,
    // so callers comparing against duration_count must use this, not nights-between.
    _inclusiveDayCount(a, b) {
      return Math.round(Math.abs(b - a) / 86400000) + 1;
    }

    /**
     * Earliest selectable delivery date, evaluated against London time:
     *   • before 11:00 Europe/London → tomorrow
     *   • from 11:00 onwards         → the day after tomorrow
     *
     * Returns a Date with the local-time Y/M/D matching the target London
     * calendar date — that's all flatpickr's minDate compares against.
     * Weekend/holiday blocks are layered on separately via the disable list.
     */
    _minSelectableDate() {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(new Date()).map((p) => [p.type, p.value])
      );
      const offset = parseInt(parts.hour, 10) < 11 ? 1 : 2;
      const result = new Date(
        parseInt(parts.year, 10),
        parseInt(parts.month, 10) - 1,
        parseInt(parts.day, 10)
      );
      result.setDate(result.getDate() + offset);
      return result;
    }
  }

  customElements.define('baboodle-pdp-block', BaboodlePdpBlock);
}
